import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Theme, ThemeContextType } from '@/types';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('hrms-theme');
    return (saved as Theme) || 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('hrms-theme', theme);
  }, [theme]);

  const toggleTheme = (): void => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
  };

  // Create MUI Theme matching Tailwind configuration
  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: theme,
      primary: {
        main: '#4f46e5', // Tailwind indigo-600
        light: '#818cf8', // Tailwind indigo-400
        dark: '#3730a3', // Tailwind indigo-800
      },
      background: {
        default: theme === 'dark' ? '#0f172a' : '#f8fafc', // slate-900 / slate-50
        paper: theme === 'dark' ? '#0f172a' : '#ffffff', // slate-900 / white
      },
      divider: theme === 'dark' ? '#1e293b' : '#f1f5f9', // slate-800 / slate-100
      text: {
        primary: theme === 'dark' ? '#f8fafc' : '#0f172a', // slate-50 / slate-900
        secondary: theme === 'dark' ? '#94a3b8' : '#64748b', // slate-400 / slate-500
      }
    },
    // Typography scale mirrors the semantic classes in src/index.css so MUI
    // <Typography> and plain HTML/Tailwind markup render an identical hierarchy.
    typography: {
      fontFamily: '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.02em' },   // hero
      h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.02em' },     // H1 / page title  → .app-h1
      h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },     // Header           → .app-header
      h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.005em' },
      h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.005em' },       // Sub-header       → .app-subheader
      h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0' },
      subtitle1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0' },   // Subtitle         → .app-subtitle
      subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0' },   // Label            → .app-label
      body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0' },
      body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0' },
      button: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.01em', textTransform: 'none' },
      caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0' },
      overline: { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.06em', textTransform: 'uppercase' }, // Caption/eyebrow → .app-caption
    },
    components: {
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: theme === 'dark' ? '#1e293b' : '#f8fafc', // slate-800 / gray-50
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme === 'dark' ? '#1e293b' : '#f1f5f9'}`,
            padding: '16px 24px', // Tailwind px-6 py-4 equivalent
          },
          head: {
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            color: theme === 'dark' ? '#94a3b8' : '#64748b',
            padding: '12px 24px', // Tailwind px-6 py-3 equivalent
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: theme === 'dark' ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
            }
          }
        }
      }
    }
  }), [theme]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
